import * as React from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router';
import {
  Checkbox,
  Icon,
  Toggle,
  Tooltip as DeprecatedTooltip,
} from '@teamsnap/teamsnap-ui';
import {
  Icon as SnapUIIcon,
  Tooltip,
  Status,
  TooltipContent,
  TooltipTrigger,
} from '@teamsnap/snap-ui';

// Local imports
import * as ShowOrganization from 'core/gql/queries/show-organizations';
import CreateAccountDrawer from 'components/form/create-account-drawer';
import { AccountStatus } from 'components/organization/organization-accounts-management';
import PaymentAccount from 'models/payment-account';
import { PaymentAccountInfo } from './types';
import { AuthContext } from 'auth';
import { hasPermissionForOrg, Permission } from '../../../core/permissions';
import { OfflinePaymentConfirmationModal } from './OfflinePaymentConfirmationModal';
import { FeatureFlagService } from 'frontend-toolkit';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import { RegistrationSteps } from './types';
import ENVIRONMENT from 'core/environment';
import { currentBaseUrl } from 'core/utils';

import './MerchantAccountSelect.scss';

interface Props {
  orgId: number;
  info: PaymentAccountInfo;
  onChange: (paymentAccountInfo: PaymentAccountInfo) => void;
  accounts: PaymentAccount[];
  formId?: number;
}

const MerchantAccountSelect: React.FC<Props> = ({
  orgId,
  info,
  onChange,
  accounts,
  formId,
}) => {
  // NOTE: Feature flag for waitlists needs to be removed once the feature is fully released
  const isOfflinePaymentsEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.OFFLINE_PAYMENTS],
    ENVIRONMENT,
    +orgId
  );

  const [showAccountsList, setShowAccountsList] = React.useState(null);
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [showOfflineModal, setShowOfflineModal] = React.useState(false);

  const history = useHistory();

  const {
    allowOfflineCheckout,
    paymentAccountId,
    organizationPaysProcessingFee,
  } = info;
  const selected = accounts?.find((a) => a.id === paymentAccountId);
  const context = React.useContext(AuthContext);
  const canCreateAccounts = hasPermissionForOrg(
    context.user,
    Permission.PaymentAccountCreate,
    orgId
  );

  const showOrgQuery = ShowOrganization.QUERY;

  const getOrganization = useQuery(showOrgQuery, {
    variables: { organizationId: +orgId },
    skip: !orgId,
  });

  const organization = getOrganization.data?.showOrganization;

  const toggleAccountsList = () => {
    setShowAccountsList(!showAccountsList);
  };

  const redirectUrl = formId
    ? `${currentBaseUrl()}/organizations/${orgId}/registration/${formId}/${
        RegistrationSteps.DEPOSIT_ACCOUNT
      }`
    : undefined;

  const accountOptionStatus = (account: PaymentAccount) => (
    <>
      {account?.status === AccountStatus.APPROVED && (
        <Status
          testId="OrganizationAccountManagement--account-option-status"
          state="success"
          text="Approved"
        />
      )}
      {account?.status === AccountStatus.RESTRICTED_SOON && (
        <>
          <Status
            testId="OrganizationAccountManagement--account-option-status"
            state="warning"
            text="Restricted soon"
          />
        </>
      )}
      {account?.status === AccountStatus.REJECTED && (
        <Status
          testId="OrganizationAccountManagement--account-option-status"
          state="negative"
          text="Rejected"
        />
      )}
      {account?.status === AccountStatus.UNDER_REVIEW && (
        <Status
          testId="OrganizationAccountManagement--account-option-status"
          state="negative"
          text="Under review"
        />
      )}
      {account?.status === AccountStatus.INFO_NEEDED && (
        <>
          <Status
            testId="OrganizationAccountManagement--account-option-status"
            state="negative"
            text={'Information required'}
          />
        </>
      )}
      {account?.status === AccountStatus.BANK_DETAILS_REQUIRED && (
        <>
          <Status
            testId="OrganizationAccountManagement--account-option-status"
            state="negative"
            text={'Bank details required'}
          />
        </>
      )}
    </>
  );

  const buildAccountsList = (accounts) => (
    <div className="accounts-list">
      {accounts.map((account) => (
        <button
          type="button"
          className="accounts-list__item u-flex u-flexJustifyBetween u-flexAlignItemsCenter"
          onClick={() =>
            onChange({
              paymentAccountId: account?.id,
              organizationPaysProcessingFee,
              allowOfflineCheckout,
            })
          }
        >
          <div className="check-column">
            {selected?.id === account.id && (
              <Icon name="check" mods="u-colorPrimary6 u-fontSizeSm" />
            )}
          </div>
          <div className="u-flexGrow1 u-flex u-flexAlignItemsCenter">
            <div>
              <>
                {account.nickname}
                {account?.bankAccountNumberLast4 && (
                  <span className="u-colorNeutral8 u-fontSizeSm u-block u-spaceTopXs">
                    *{account?.bankAccountNumberLast4}
                  </span>
                )}
              </>
            </div>
          </div>
          {accountOptionStatus(account)}
        </button>
      ))}
      {canCreateAccounts && (
        <button
          className="accounts-list__item"
          type="button"
          onClick={() => {
            setShowAccountsList(false);
            setShowDrawer(true);
          }}
        >
          <Icon
            name="plus"
            mods="u-colorNeutral9 u-fontSizeLg u-spaceRightSm"
          />
          <strong>Add a merchant account</strong>
        </button>
      )}
    </div>
  );

  const handleBodyClick = React.useCallback((e) => {
    e.stopPropagation();
    const isTargetingPopup =
      e.target.closest('#merchant-account-select') != null;
    if (!isTargetingPopup) {
      setShowAccountsList(false);
    }
  }, []);

  React.useEffect(() => {
    if (!showAccountsList) {
      window.removeEventListener('click', handleBodyClick, false);
    } else {
      window.addEventListener('click', handleBodyClick, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAccountsList]);

  return (
    <>
      {showOfflineModal ? (
        <OfflinePaymentConfirmationModal
          show={showOfflineModal}
          onEnabled={() => {
            onChange({
              paymentAccountId,
              organizationPaysProcessingFee,
              allowOfflineCheckout: !allowOfflineCheckout,
            });
            setShowOfflineModal(false);
          }}
          onRedirect={() => {
            setShowOfflineModal(false);

            // This replaces the step portion of the URL with the step that has the discount code step number
            const redirectPath = `${history.location.pathname
              .split('/')
              .slice(0, 5)
              .join('/')}/${RegistrationSteps.DISCOUNTS}`;

            history.push(redirectPath, {
              prevPath: history.location.pathname,
            });
          }}
        />
      ) : null}

      <CreateAccountDrawer
        organization={organization}
        isDrawOpen={showDrawer}
        onDrawClose={() => setShowDrawer(false)}
        createEditCallback={(account) => {
          if (account?.status === AccountStatus.APPROVED) {
            onChange({
              paymentAccountId: account?.id,
              organizationPaysProcessingFee,
              allowOfflineCheckout,
            });
          }
        }}
        redirectUrl={redirectUrl}
      />
      {!accounts ? (
        <div className="Loader-indicator">
          <span className="SpinAnimation" />
          <div className="Loader-indicatorText">
            Loading payment accounts...
          </div>
        </div>
      ) : (
        <div id="merchant-account-select">
          <button
            className={`${!paymentAccountId ? 'u-colorNeutral6' : ''} ${
              showAccountsList ? 'account-list__trigger--active' : ''
            } account-list__trigger`}
            type="button"
            onClick={toggleAccountsList}
          >
            <div className="u-sizeFull u-flex u-flexJustifyBetween u-flexAlignItemsCenter">
              <span>
                {selected
                  ? selected.nickname
                  : 'Select or create a deposit account'}
              </span>
              <span className="u-colorNeutral8 u-fontSizeSm u-block u-spaceTopXs u-padRightMd">
                {selected?.bankAccountNumberLast4
                  ? `*${selected?.bankAccountNumberLast4}`
                  : ''}
              </span>
            </div>
            <Icon name="down" mods="u-colorNeutral9 u-fontSizeLg" />
          </button>
          {showAccountsList && buildAccountsList(accounts)}
        </div>
      )}
      <Checkbox
        name="pass-processing-fee"
        mods="u-padTopMd"
        label={
          <span>
            Pass online fee to registrants
            <DeprecatedTooltip
              mods="u-padLeftSm"
              type="icon"
              text="If this box is selected, the online fees will appear on the registrant's total cost page. Online fee amounts vary by payment method."
            >
              <Icon name="info" />
            </DeprecatedTooltip>
          </span>
        }
        inputProps={{
          checked: !organizationPaysProcessingFee,
          onChange: () =>
            onChange({
              paymentAccountId,
              organizationPaysProcessingFee: !organizationPaysProcessingFee,
              allowOfflineCheckout,
            }),
        }}
      />
      {isOfflinePaymentsEnabled && (
        <div className="sui-flex">
          <Toggle
            name="toggle-offline-payments"
            style={{ color: 'green' }}
            testId="toggle-offline-payments-id"
            inputProps={{
              onChange: () => {},
              checked: allowOfflineCheckout,
              onClick: () => {
                if (!allowOfflineCheckout) {
                  setShowOfflineModal(true);
                } else {
                  onChange({
                    paymentAccountId,
                    organizationPaysProcessingFee,
                    allowOfflineCheckout: !allowOfflineCheckout,
                  });
                }
              },
            }}
          />

          <div className="sui-ml-1">Enable cash payments</div>

          <div>
            <Tooltip>
              <TooltipTrigger asChild>
                <SnapUIIcon
                  size="s"
                  className="sui-ml-0.5 sui-text-blue-40"
                  name="info"
                />
              </TooltipTrigger>
              <TooltipContent>
                If enabled:
                <ul className="sui-mx-2">
                  <li>
                    Registrants who select cash payment can complete checkout
                    without paying online.
                  </li>
                  <li>Cash registrants cannot choose payment plans.</li>
                  <li>
                    You are indicating that your organization will directly
                    collect payments from registrants via cash.
                  </li>
                  <li>
                    Cash transactions will not be automatically recorded and
                    must be manually entered.
                  </li>
                </ul>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}
    </>
  );
};

export default MerchantAccountSelect;
